import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { Box, Text } from '@mantine/core'
import { FiX } from 'react-icons/fi'
import { format, isValid } from 'date-fns'
import { useStore } from '../../stores/store'
import agent from '../../api/agent'
import { isMotdLocallyDismissedToday, persistMotdLocalDismiss } from '../motdLocalDismiss'

/** ~Bootstrap col-lg-5 width at common breakpoints */
const MOTD_MAX_WIDTH_PX = 500

export type MotdBannerLayout = 'app' | 'landing';

type MotdBannerProps = { layout?: MotdBannerLayout };

export default observer(function MotdBanner ({ layout = 'app' }: MotdBannerProps) {
  const { commonStore, userStore } = useStore()
  const { currentMotd } = commonStore
  const [dismissedThisVisit, setDismissedThisVisit] = useState(false)

  const visible = useMemo(() => {
    if (dismissedThisVisit) return false
    if (!currentMotd?.message?.trim()) return false
    if (currentMotd.isDismissed) return false
    if (isMotdLocallyDismissedToday(currentMotd.id)) return false
    return true
  }, [currentMotd, dismissedThisVisit])

  if (!visible || !currentMotd) {
    return null
  }

  const setAt = currentMotd.setAt ? new Date(currentMotd.setAt) : null
  const dateLabel = setAt && isValid(setAt) ? format(setAt, 'MMM d, yyyy') : '—'

  const handleDismiss = async () => {
    setDismissedThisVisit(true)
    persistMotdLocalDismiss(currentMotd.id)
    if (userStore.isLoggedIn) {
      try {
        await agent.Motd.dismiss()
        await commonStore.loadMotd()
      } catch {
        /* interceptor may toast; banner stays hidden via localStorage */
      }
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: layout === 'landing' ? 16 : 12,
        paddingBottom: 4
      }}
    >
      <Box
        role="region"
        aria-label="Message of the day"
        sx={{
          width: '100%',
          maxWidth: MOTD_MAX_WIDTH_PX,
          boxSizing: 'border-box',
          padding: '8px 38px 8px 12px',
          borderRadius: 12,
          background:
            'linear-gradient(180deg, var(--gs-motd-banner-bg-strong) 0%, var(--gs-motd-banner-bg) 100%)',
          backdropFilter: 'saturate(1.08)',
          border: '1px solid var(--gs-motd-banner-border)',
          boxShadow:
            '0 10px 40px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.07)',
          position: 'relative'
        }}
      >
        <Text
          component="div"
          weight={700}
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 9,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--gs-motd-banner-label)',
            marginBottom: 4,
            lineHeight: 1.3
          }}
        >
          Message of the day · {dateLabel}
          {currentMotd.setByUsername ? ` · ${currentMotd.setByUsername}` : ''}
        </Text>
        <Text
          component="p"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            lineHeight: 1.45,
            color: 'var(--gs-text)',
            margin: 0,
            paddingRight: 4,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
        >
          {currentMotd.message.trim()}
        </Text>

        <button
          type="button"
          aria-label="Dismiss message for the rest of today"
          onClick={() => void handleDismiss()}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--gs-motd-banner-dismiss-bg)',
            border: '1px solid var(--gs-motd-banner-border)',
            borderRadius: 8,
            color: 'var(--gs-muted)',
            cursor: 'pointer'
          }}
        >
          <FiX size={16} />
        </button>
      </Box>
    </Box>
  )
})
